﻿
import { defineComponent } from 'vue';
import { response_booking } from "../../../tms/booking_engine/objects/v1_3/booking/search/response_booking";
import { Booking } from "../../booking_engine/services/booking";
import { Date_Service } from "../../system/services/date_service";
import details_dialog from "./details/details_dialog.vue";
import { Booking_Status_Class } from "./booking_status";

let Book: Booking = new Booking();
let Date_Services: Date_Service = new Date_Service();

type SortKey = 'departure' | 'id' | 'reference' | 'lead' | 'booking' | 'consultant' | 'status' | 'trip';

const Columns: { key: SortKey, label: string, optional?: boolean }[] = [
    { key: 'trip',       label: 'Trip' },
    { key: 'departure',  label: 'Departure' },
    { key: 'id',         label: 'Booking ID' },
    { key: 'reference',  label: 'Agent Ref' },
    { key: 'lead',       label: 'Lead Guest' },
    { key: 'booking',    label: 'Booked', optional: true },
    { key: 'consultant', label: 'Consultant', optional: true },
    { key: 'status',     label: 'Status' }
];

function Empty_Filters(): Record<SortKey, string> {
    return { trip: '', departure: '', id: '', reference: '', lead: '', booking: '', consultant: '', status: '' };
}

export default defineComponent({
    components: {
        details_dialog
    },
    emits: ['bookings_changed'],
    props: {
        bookings: {
            type: Array as () => response_booking[] | undefined,
            required: false
        }
    },
    data() {
        return {
            sortKey: 'departure' as SortKey,
            sortAsc: true,
            searching: false,
            namesExpandedAll: false,
            loadingIndex: -1,
            columns: Columns,
            filters: Empty_Filters(),
            activeFilter: null as SortKey | null,
            detailsOpen: false
        }
    },
    methods: {
        Lead_Passenger(b: response_booking): string {
            const p = b?.passengers?.[0];
            return (p?.first_name ?? "") + " " + (p?.family_name ?? "");
        },
        Trip_Name(b: response_booking): string {
            return b?.trips?.[0]?.trip_name ?? "";
        },
        Short_Trip_Name(b: response_booking, maxLen = 24): string {
            const full = this.Trip_Name(b);
            return full.length <= maxLen ? full : full.slice(0, maxLen - 1) + '…';
        },
        Toggle_Names_All() {
            this.namesExpandedAll = !this.namesExpandedAll;
        },
        Display_Trip_Name(b: response_booking, maxLen = 24): string {
            return this.namesExpandedAll ? this.Trip_Name(b) : this.Short_Trip_Name(b, maxLen);
        },
        Departure_Date_Display(b: response_booking): string {
            return Date_Services.display_date(b?.trips?.[0]?.departure_date ?? "");
        },
        Booking_Date_Display(b: response_booking): string {
            return Date_Services.display_date(b?.trips?.[0]?.booking_date ?? "");
        },
        Booking_ID(b: response_booking): string {
            return String((b as any)?.group_booking_id ?? (b as any)?.id ?? "");
        },
        Agent_Reference(b: response_booking): string {
            return (b as any)?.agent_reference ?? "";
        },
        Get_Consultant(b: response_booking): string {
            return b?.trips?.[0]?.affiliate_contact ?? "";
        },
        Booking_Status(b: response_booking): string {
            return b?.trips?.[0]?.passengers?.[0]?.status ?? "";
        },
        setSort(key: SortKey) {
            if (this.sortKey === key) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
        },
        Is_Filter_Open(key: SortKey): boolean {
            return this.activeFilter === key || this.filters[key] !== '';
        },
        Open_Filter(key: SortKey) {
            this.activeFilter = key;
            this.$nextTick(() => {
                const input = (this.$el as HTMLElement).querySelector<HTMLInputElement>(`input[data-filter="${key}"]`);
                input?.focus();
            });
        },
        Close_Filter(key: SortKey) {
            if (this.activeFilter === key) {
                this.activeFilter = null;
            }
        },
        Clear_Filter(key: SortKey) {
            this.filters[key] = '';
            this.activeFilter = null;
        },
        Clear_All_Filters() {
            this.filters = Empty_Filters();
            this.activeFilter = null;
        },
        Status_Class(b: response_booking): string {
            return Booking_Status_Class(this.Booking_Status(b));
        },
        valueForFilter(b: response_booking, key: SortKey): string {
            switch (key) {
            case 'trip':        return this.Trip_Name(b);
            case 'departure':   return this.Departure_Date_Display(b);
            case 'id':          return this.Booking_ID(b);
            case 'reference':   return this.Agent_Reference(b);
            case 'lead':        return this.Lead_Passenger(b);
            case 'booking':     return this.Booking_Date_Display(b);
            case 'consultant':  return this.Get_Consultant(b);
            case 'status':      return this.Booking_Status(b);
            default: return '';
            }
        },
        valueForSort(b: response_booking): string | number {
            switch (this.sortKey) {
            case 'lead':        return this.Lead_Passenger(b).toLowerCase();
            case 'id':          return this.Booking_ID(b).toLowerCase();
            case 'reference':   return this.Agent_Reference(b).toLowerCase();
            case 'consultant':  return this.Get_Consultant(b).toLowerCase();
            case 'status':      return this.Booking_Status(b).toLowerCase();
            case 'trip':        return this.Trip_Name(b).toLowerCase();
            case 'departure': {
                const d = b?.trips?.[0]?.departure_date ?? "";
                const t = d ? new Date(d).getTime() : 0;
                return isNaN(t) ? 0 : t;
            }
            case 'booking': {
                const d = b?.trips?.[0]?.booking_date ?? "";
                const t = d ? new Date(d).getTime() : 0;
                return isNaN(t) ? 0 : t;
            }
            default: return 0;
            }
        },
        async Booking_Details(b: response_booking, index: number) {
            const id = (b as any)?.group_booking_id ?? (b as any)?.id;
            if (!id)
            { 
                return;
            }
            this.loadingIndex = index;
            try {
                const details = await Book.Get_Book(id, this.$store.getters.get_Agent_Login);
                this.$store.commit("Setup_Booking", { Booking_Details: details });
                this.$store.commit("Initialise_Booking_Cancellation");
                this.detailsOpen = true;
            } finally {
                this.loadingIndex = -1;
            }
        },
        Close_Details(changed: boolean) {
            this.detailsOpen = false;
            if (changed) {
                this.$emit('bookings_changed');
            }
        },
        Row_Key(b: response_booking, index: number): string {
            return this.Booking_ID(b) || index.toString();
        }
    },
    computed: {
        Active_Filter_Count(): number {
            return Columns.filter(c => this.filters[c.key].trim() !== '').length;
        },
        Filtered_Bookings(): response_booking[] {
            const active = Columns
                .map(c => ({ key: c.key, term: this.filters[c.key].trim().toLowerCase() }))
                .filter(f => f.term !== '');
            const list = this.bookings ?? [];
            if (active.length === 0) {
                return list.slice();
            }
            return list.filter(b => active.every(f => this.valueForFilter(b, f.key).toLowerCase().includes(f.term)));
        },
        Sorted_Bookings(): response_booking[] {
            const list = this.Filtered_Bookings;
            const asc = this.sortAsc ? 1 : -1;
            list.sort((a, b) => {
                const va = this.valueForSort(a);
                const vb = this.valueForSort(b);
                if (va < vb)
                {
                    return -1 * asc;
                }
                if (va > vb){
                    return 1 * asc;
                }
                return 0;
            });
            return list;
        }
    }
});